import { Button, Radio, Select, Tag, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toLower, uniqBy } from "lodash-es";

const { Text } = Typography;

const getAppColor = (appName) => {
  const lowerAppName = toLower(appName);
  if (lowerAppName === "kledo") return "blue";
  if (lowerAppName === "gajihub") return "pink";
  return "#2759db";
};

// Nomor disamakan dengan input telepon form (meetingMode): angka saja.
const getUserPhone = (user) =>
  String(user?.phone_number ?? user?.phone ?? "").replace(/[^\d]/g, "");

// id perusahaan hanya unik per aplikasi: id yang sama bisa muncul di app_name
// berbeda (mis. Kledo & CRM.id). Key gabungan dipakai sebagai value Select
// supaya tidak bentrok di virtual list (bikin opsi kacau saat discroll).
const getCompanyKey = (company) => `${company?.app_name}-${company?.id}`;

const getUniqueCompanies = (user) =>
  uniqBy(user?.companies || [], getCompanyKey);

const getDefaultCompanyKey = (user) => {
  const companies = getUniqueCompanies(user);
  return companies.length === 1 ? getCompanyKey(companies[0]) : null;
};

// Pilihan data kontak dari profil inhouse (users /users/inhouseProfile).
// User > 1 → pilih lewat radio; perusahaan > 1 → wajib pilih sebelum apply.
// Data hanya diisikan ke form saat tombol ditekan, tidak otomatis.
const InhouseContactPicker = ({ users = [], onApply }) => {
  const { t } = useTranslation();
  const [userIndex, setUserIndex] = useState(0);
  const [companyKey, setCompanyKey] = useState(() =>
    getDefaultCompanyKey(users[0]),
  );

  const selectedUser = users[userIndex];
  const companies = getUniqueCompanies(selectedUser);
  const isMultipleUser = users.length > 1;
  const isCompanyRequired = companies.length > 1 && !companyKey;

  const onChangeUser = (e) => {
    const index = e.target.value;
    setUserIndex(index);
    setCompanyKey(getDefaultCompanyKey(users[index]));
  };

  const onApplyHandler = () => {
    const company = companies.find((row) => getCompanyKey(row) === companyKey);
    onApply({
      name: selectedUser?.name,
      phone: getUserPhone(selectedUser),
      email: selectedUser?.email,
      company: company?.name,
    });
  };

  if (!selectedUser) return null;

  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
      <Text strong className="block mb-2">
        {t("inhouse_contact.title")}
      </Text>
      {isMultipleUser ? (
        <>
          <Text type="secondary" className="block mb-2">
            {t("inhouse_contact.multiple_users", { count: users.length })}
          </Text>
          <Radio.Group
            value={userIndex}
            onChange={onChangeUser}
            className="flex flex-col gap-2 max-h-48 overflow-y-auto mb-3"
          >
            {users.map((user, index) => (
              <Radio key={user.id ?? index} value={index}>
                <div className="flex flex-col">
                  <Text>{user.name}</Text>
                  <Text type="secondary" className="text-xs">
                    {[getUserPhone(user), user.email]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                </div>
              </Radio>
            ))}
          </Radio.Group>
        </>
      ) : (
        <div className="flex flex-col mb-3">
          <Text>{selectedUser.name}</Text>
          <Text type="secondary" className="text-xs">
            {[getUserPhone(selectedUser), selectedUser.email]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        </div>
      )}
      {companies.length > 1 && (
        <Select
          className="w-full mb-3"
          placeholder={t("inhouse_contact.select_company")}
          value={companyKey}
          onChange={setCompanyKey}
          options={companies.map((company) => ({
            value: getCompanyKey(company),
            label: (
              <span>
                {company.name}{" "}
                <Tag color={getAppColor(company.app_name)}>
                  {company.app_name}
                </Tag>
              </span>
            ),
          }))}
        />
      )}
      {companies.length === 1 && (
        <Text type="secondary" className="block mb-3 text-xs">
          {t("inhouse_contact.company")}: {companies[0].name}
        </Text>
      )}
      <div className="flex justify-end">
        <Button
          type="primary"
          size="small"
          onClick={onApplyHandler}
          disabled={isCompanyRequired}
        >
          {t("inhouse_contact.apply")}
        </Button>
      </div>
    </div>
  );
};

export default InhouseContactPicker;
